import type { LucideIcon } from "lucide-react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { APP_VERSION } from "src/appVersion";
import { UserAvatar } from "src/components/UserAvatar";
import { Constants } from "src/constant";
import { getAppNavItems } from "src/lib/appNav";
import type { AppDispatch } from "src/store";
import {
  selectUser,
  selectUserProfile,
  signOutUser,
} from "src/store/authSlice";
import {
  reset as resetPresence,
  stopPresenceListeners,
} from "src/store/presenceSlice";
import { selectTheme, setTheme, type ThemeKey } from "src/store/themeSlice";
import { AppSidebarAccountMenu } from "./AppSidebarAccountMenu";

function NavRow({
  to,
  label,
  icon: Icon,
  end,
  layout,
  showLabels,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  layout: "rail" | "drawer";
  showLabels: boolean;
  onNavigate?: () => void;
}) {
  const labelVisible = showLabels ? "inline truncate" : "hidden";
  const itemJustify = showLabels
    ? "justify-start gap-3"
    : layout === "rail"
      ? "justify-center gap-0 px-2"
      : "justify-start gap-3";

  const wrapRef = useRef<HTMLDivElement>(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState({ left: 0, top: 0 });

  const updateFlyoutPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setFlyoutPos({ left: r.right + 8, top: r.top + r.height / 2 });
  }, []);

  useLayoutEffect(() => {
    if (!flyoutOpen || layout !== "rail" || showLabels) return;
    updateFlyoutPosition();
    const nav = wrapRef.current?.closest("nav");
    const onScrollOrResize = () => updateFlyoutPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    nav?.addEventListener("scroll", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      nav?.removeEventListener("scroll", onScrollOrResize);
    };
  }, [flyoutOpen, layout, showLabels, updateFlyoutPosition]);

  const showFlyout = layout === "rail" && !showLabels && flyoutOpen;

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => {
        if (layout === "rail" && !showLabels) {
          updateFlyoutPosition();
          setFlyoutOpen(true);
        }
      }}
      onMouseLeave={() => setFlyoutOpen(false)}
      onFocus={() => {
        if (layout === "rail" && !showLabels) {
          updateFlyoutPosition();
          setFlyoutOpen(true);
        }
      }}
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget as Node)) {
          setFlyoutOpen(false);
        }
      }}
    >
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        className={({ isActive }) =>
          [
            "flex min-h-11 items-center rounded-2xl border-4 px-3 py-2 text-sm font-extrabold transition-[background-color,box-shadow,border-color,color,transform] outline-none focus-visible:ring-4 focus-visible:ring-[#14B8A6]/45",
            itemJustify,
            isActive
              ? "border-[#14B8A6]/50 bg-[#14B8A6]/20 text-white shadow-[0_5px_0_#0D9488/55] active:translate-y-0.5"
              : "border-transparent text-gray-400 shadow-none hover:border-white/12 hover:bg-white/8 hover:text-gray-100 hover:shadow-[0_4px_0_rgba(0,0,0,0.3)]",
          ].join(" ")
        }
      >
        <Icon className="h-5 w-5 shrink-0 opacity-90" aria-hidden />
        <span className={labelVisible}>{label}</span>
      </NavLink>
      {showFlyout &&
        createPortal(
          <span
            role="tooltip"
            className="pointer-events-none fixed z-[200] -translate-y-1/2 whitespace-nowrap rounded-2xl border-4 border-white/15 bg-gray-900/95 px-3 py-2 text-xs font-extrabold text-gray-100 shadow-[0_6px_0_rgba(0,0,0,0.45)] backdrop-blur-md"
            style={{ left: flyoutPos.left, top: flyoutPos.top }}
          >
            {label}
          </span>,
          document.body,
        )}
    </div>
  );
}

interface AppSidebarProps {
  isOnline: boolean;
  layout: "rail" | "drawer";
  logoSrc?: string;
  /** Drawer only: close overlay after navigation or explicit close */
  onClose?: () => void;
  /** Rail only: wide sidebar with labels vs icon-only strip */
  railExpanded?: boolean;
  /** Rail only: Firebase-style collapse / expand toggle */
  onToggleRailExpanded?: () => void;
}

const publicUrl = process.env.PUBLIC_URL ?? "";
const defaultBrandLogo = `${publicUrl}/iVueit.png`;
const fallbackBrandLogo = `${publicUrl}/logo512.png`;

async function clearClientCachedData(): Promise<void> {
  try {
    localStorage.clear();
  } catch {
    // ignore cache clearing failures
  }

  try {
    sessionStorage.clear();
  } catch {
    // ignore cache clearing failures
  }

  try {
    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }
  } catch {
    // ignore cache clearing failures
  }

  try {
    const indexedDbWithDatabases = indexedDB as IDBFactory & {
      databases?: () => Promise<Array<{ name?: string }>>;
    };
    if (typeof indexedDbWithDatabases.databases === "function") {
      const dbs = await indexedDbWithDatabases.databases();
      await Promise.all(
        dbs.map((db) => {
          if (!db.name) return Promise.resolve();
          return new Promise<void>((resolve) => {
            const request = indexedDB.deleteDatabase(db.name!);
            request.onsuccess = () => resolve();
            request.onerror = () => resolve();
            request.onblocked = () => resolve();
          });
        }),
      );
    }
  } catch {
    // ignore cache clearing failures
  }
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  isOnline,
  layout,
  logoSrc = defaultBrandLogo,
  onClose,
  railExpanded = true,
  onToggleRailExpanded,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const currentTheme = useSelector(selectTheme);

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountMenuPanelId = useId();
  const accountMenuButtonId = useId();

  const navItems = userProfile ? getAppNavItems(userProfile.role) : [];

  useEffect(() => {
    if (!accountMenuOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const el = accountMenuRef.current;
      if (el && !el.contains(e.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAccountMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [accountMenuOpen]);

  const handleLogout = async () => {
    setAccountMenuOpen(false);
    onClose?.();
    try {
      await dispatch(stopPresenceListeners());
      dispatch(resetPresence());
      await dispatch(signOutUser()).unwrap();
      await clearClientCachedData();
      navigate(Constants.LOGIN_ROUTE);
    } catch (e) {
      console.error("Failed to sign out:", e);
    }
  };

  const handleThemeChange = useCallback(
    (theme: ThemeKey) => {
      dispatch(setTheme(theme));
    },
    [dispatch],
  );

  const onNavigate = layout === "drawer" ? onClose : undefined;

  const showLabels = layout === "drawer" || railExpanded;

  const railWidthClass =
    layout === "rail"
      ? railExpanded
        ? "w-64 shrink-0"
        : "w-[4.5rem] shrink-0"
      : "w-full min-w-0";

  return (
    <div
      className={[
        "relative flex h-full min-h-0 flex-col overflow-x-visible bg-gray-950/50 font-['Nunito',system-ui,sans-serif] backdrop-blur-xl transition-[width] duration-200 ease-out motion-reduce:transition-none",
        layout === "rail" ? "border-r-4 border-white/10" : "",
        railWidthClass,
      ].join(" ")}
    >
      {/* Brand */}
      <div
        className={`px-2.5 pb-1 pt-3 sm:pt-2.5 ${
          layout === "rail" ? (showLabels ? "px-3" : "px-2.5") : "px-3"
        }`}
      >
        <div
          className={[
            "flex items-center gap-3 rounded-2xl border-4 border-white/10 bg-white/[0.06] p-3.5 shadow-[0_6px_0_rgba(0,0,0,0.38)]",
            layout === "rail" && !showLabels
              ? "flex-col gap-2 py-3"
              : "flex-row",
            layout === "drawer" ? "flex-wrap" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {layout === "drawer" && (
            <button
              type="button"
              onClick={onClose}
              className="min-h-11 min-w-11 inline-flex shrink-0 items-center justify-center rounded-2xl border-4 border-white/12 bg-white/5 p-0 text-gray-300 shadow-[0_4px_0_rgba(0,0,0,0.35)] transition-[transform,background-color,box-shadow] hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-[#14B8A6]/45 active:translate-y-0.5 md:hidden"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          )}
          <img
            src={logoSrc}
            alt=""
            className="h-10 w-10 shrink-0 rounded-2xl border-4 border-white/20 object-cover shadow-[0_4px_0_rgba(0,0,0,0.3)]"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              if (
                img.src.endsWith("logo512.png") ||
                img.src.includes("/logo512.png")
              )
                return;
              img.src = fallbackBrandLogo;
            }}
          />
          <div
            className={`min-w-0 flex-1 ${
              layout === "rail"
                ? showLabels
                  ? "block text-left"
                  : "hidden"
                : "block"
            }`}
          >
            <p className="truncate text-sm font-extrabold tracking-tight text-white">
              Live Presence
            </p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav
        className="flex-1 space-y-2 overflow-y-auto px-2.5 py-2 sm:px-3"
        aria-label="Main navigation"
      >
        {navItems.map((item) => (
          <NavRow
            key={item.key}
            to={item.to}
            label={item.label}
            icon={item.icon}
            end={item.end}
            layout={layout}
            showLabels={showLabels}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      <div className="relative mt-auto shrink-0 border-t-4 border-white/10 px-2 pb-2 pt-1">
        {layout === "rail" && onToggleRailExpanded && (
          <button
            type="button"
            onClick={onToggleRailExpanded}
            aria-label={
              railExpanded ? "Collapse navigation" : "Expand navigation"
            }
            title={railExpanded ? "Collapse sidebar" : "Expand sidebar"}
            className="absolute -top-5 right-0 z-40 flex h-11 w-11 translate-x-1/2 items-center justify-center rounded-full border-4 border-white/18 bg-gray-900/95 text-gray-100 shadow-[0_5px_0_rgba(0,0,0,0.55)] ring-0 backdrop-blur-sm transition-[transform,background-color,border-color,box-shadow] hover:border-[#14B8A6]/40 hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-[#14B8A6]/50 active:translate-y-0.5"
          >
            {railExpanded ? (
              <ChevronLeft className="h-5 w-5" aria-hidden />
            ) : (
              <ChevronRight className="h-5 w-5" aria-hidden />
            )}
          </button>
        )}
        <div className="flex items-center justify-center font-bold mb-2">
          <p className="text-xs text-[#777777]">v{APP_VERSION}</p>
        </div>
        {user && userProfile && (
          <div ref={accountMenuRef} className="relative p-1.5 sm:p-2">
            {/* version info */}
            <button
              type="button"
              onClick={() => setAccountMenuOpen((o) => !o)}
              aria-expanded={accountMenuOpen}
              aria-haspopup="menu"
              aria-controls={accountMenuPanelId}
              id={accountMenuButtonId}
              className={[
                "flex min-h-12 w-full items-center rounded-2xl border-4 py-2.5 text-left shadow-[0_5px_0_rgba(0,0,0,0.3)] transition-[transform,background-color,box-shadow,border-color] hover:bg-white/12 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#14B8A6]/45 active:translate-y-0.5",
                isOnline
                  ? "border-white/12 bg-white/[0.07]"
                  : "border-rose-500/40 bg-rose-500/[0.12]",
                layout === "rail"
                  ? showLabels
                    ? "justify-start gap-3 px-3"
                    : "justify-center gap-0 px-2"
                  : "gap-3 px-3",
              ].join(" ")}
            >
              <UserAvatar user={user} size="md" />
              <span
                className={`min-w-0 flex-1 truncate text-sm font-extrabold text-white ${
                  layout === "rail" && !showLabels ? "hidden" : "block"
                }`}
              >
                {user.displayName || user.email?.split("@")[0]}
              </span>
            </button>
            {accountMenuOpen && (
              <AppSidebarAccountMenu
                user={user}
                userProfile={userProfile}
                layout={layout}
                showLabels={showLabels}
                accountMenuPanelId={accountMenuPanelId}
                accountMenuButtonId={accountMenuButtonId}
                currentTheme={currentTheme}
                onThemeChange={handleThemeChange}
                onLogout={handleLogout}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppSidebar;
